// src/features/monetize/useMonetizeMutations.ts

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MONETIZE_QUERY_KEYS } from '@/lib/constants/monetize';
import type {
  CancelSubscriptionReq,
  CompReq,
  MailSenderReq,
  MembershipTemplateReq,
  MonetizeEntitlement,
  MonetizeProfileReq,
  OfferReq,
  OfferUpdateReq,
  OnboardingReq,
  PriceReq,
  ProductReq,
  ProductStatus,
  RefundReq,
  TicketedLiveTemplateReq,
} from '@/lib/types/Monetize';
import { MonetizeApi } from './MonetizeApi';

/**
 * One mutation shape for every Monetize write: the selected account is the
 * operator, and the catalog (or the status, for profile and payouts) is
 * re-read afterwards. Callers surface `gatingSync` from the answer.
 */
function useMonetizeMutation<TArgs, TRes>(
  run: (cId: number, args: TArgs) => Promise<TRes>,
  invalidates: 'catalog' | 'status' | 'both',
) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: TArgs) => {
      if (!selectedCId) throw new Error('No account selected');
      return run(selectedCId, args);
    },
    onSuccess: () => {
      if (!selectedCId) return;
      if (invalidates !== 'status') queryClient.invalidateQueries({ queryKey: MONETIZE_QUERY_KEYS.catalog(selectedCId) });
      if (invalidates !== 'catalog') queryClient.invalidateQueries({ queryKey: MONETIZE_QUERY_KEYS.status(selectedCId) });
    },
  });
}

export function useCreateProduct() {
  return useMonetizeMutation((cId, req: ProductReq) => MonetizeApi.createProduct(cId, req), 'catalog');
}

export function useUpdateProduct() {
  return useMonetizeMutation(
    (cId, { productId, req }: { productId: string; req: ProductReq }) => MonetizeApi.updateProduct(cId, productId, req),
    'catalog',
  );
}

export function useSetProductStatus() {
  return useMonetizeMutation(
    (cId, { productId, status }: { productId: string; status: ProductStatus }) => MonetizeApi.setProductStatus(cId, productId, status),
    'catalog',
  );
}

export function useAddPrice() {
  return useMonetizeMutation(
    (cId, { productId, req }: { productId: string; req: PriceReq }) => MonetizeApi.addPrice(cId, productId, req),
    'catalog',
  );
}

export function useArchivePrice() {
  return useMonetizeMutation((cId, priceId: string) => MonetizeApi.archivePrice(cId, priceId), 'catalog');
}

export function useCreateOffer() {
  return useMonetizeMutation((cId, req: OfferReq) => MonetizeApi.createOffer(cId, req), 'catalog');
}

export function useUpdateOffer() {
  return useMonetizeMutation(
    (cId, { offerId, req }: { offerId: string; req: OfferUpdateReq }) => MonetizeApi.updateOffer(cId, offerId, req),
    'catalog',
  );
}

export function useArchiveOffer() {
  return useMonetizeMutation((cId, offerId: string) => MonetizeApi.archiveOffer(cId, offerId), 'catalog');
}

export function useApplyMembership() {
  return useMonetizeMutation((cId, req: MembershipTemplateReq) => MonetizeApi.applyMembership(cId, req), 'catalog');
}

export function useApplyTicketedLive() {
  return useMonetizeMutation((cId, req: TicketedLiveTemplateReq) => MonetizeApi.applyTicketedLive(cId, req), 'catalog');
}

export function useSyncGating() {
  return useMonetizeMutation((cId, _: void) => MonetizeApi.syncGating(cId), 'catalog');
}

export function useSaveProfile() {
  return useMonetizeMutation((cId, req: MonetizeProfileReq) => MonetizeApi.saveProfile(cId, req), 'both');
}

export function useSetMailSender() {
  return useMonetizeMutation((cId, req: MailSenderReq) => MonetizeApi.setMailSender(cId, req), 'status');
}

export function useVerifyMailSender() {
  return useMonetizeMutation((cId, _: void) => MonetizeApi.verifyMailSender(cId), 'status');
}

export function useStartOnboarding() {
  return useMonetizeMutation((cId, req: OnboardingReq) => MonetizeApi.startOnboarding(cId, req), 'status');
}

export function useRefreshConnect() {
  return useMonetizeMutation((cId, _: void) => MonetizeApi.getConnect(cId, true), 'status');
}

// ---- the viewer's ledger (Seq 1 step 4): every write re-reads that viewer ----

function useViewerMutation<TArgs extends { viewerId: string }, TRes>(run: (cId: number, args: TArgs) => Promise<TRes>) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: TArgs) => {
      if (!selectedCId) throw new Error('No account selected');
      return run(selectedCId, args);
    },
    onSuccess: (_res, args) => {
      if (selectedCId) queryClient.invalidateQueries({ queryKey: MONETIZE_QUERY_KEYS.ledger(selectedCId, args.viewerId) });
    },
  });
}

/** One grant per scope, in order; the answer is every row written. */
export function useComp() {
  return useViewerMutation(async (cId, { viewerId, scopes, req }: { viewerId: string; scopes: string[]; req: Omit<CompReq, 'scope'> }) => {
    const rows: MonetizeEntitlement[] = [];
    for (const scope of scopes) rows.push(await MonetizeApi.comp(cId, viewerId, { ...req, scope }));
    return rows;
  });
}

export function useRevokeEntitlement() {
  return useViewerMutation((cId, { entitlementId, reason }: { viewerId: string; entitlementId: string; reason: string }) => MonetizeApi.revokeEntitlement(cId, entitlementId, reason));
}

export function useExtendEntitlement() {
  return useViewerMutation((cId, { entitlementId, endsAt, reason }: { viewerId: string; entitlementId: string; endsAt: string; reason: string }) => MonetizeApi.extendEntitlement(cId, entitlementId, endsAt, reason));
}

export function useRefundPurchase() {
  return useViewerMutation((cId, { purchaseId, req }: { viewerId: string; purchaseId: string; req: RefundReq }) => MonetizeApi.refundPurchase(cId, purchaseId, req));
}

export function useCancelSubscription() {
  return useViewerMutation((cId, { subscriptionId, req }: { viewerId: string; subscriptionId: string; req: CancelSubscriptionReq }) => MonetizeApi.cancelSubscription(cId, subscriptionId, req));
}
